import DatadogService from '@services/DatadogService';
import { CLIENT_I18N_KEY } from '@features/event-level-column-rename/constants';
import type {
  CanonicalColumnId,
  ColumnLabelResolver,
  LineItemColumnLabels,
  TranslateLabel,
} from '@features/event-level-column-rename/types';

const reportedMissingIds = new Set<CanonicalColumnId>();

/**
 * Reports a known canonical id that was absent from a map the backend did send — a producer
 * bug worth a signal.
 *
 * Logs at most once per column id per page load: the resolver runs on every grid repaint, so
 * an unguarded log would bury the very signal it exists to raise.
 */
const reportMissingColumnId = (columnId: CanonicalColumnId): void => {
  if (reportedMissingIds.has(columnId)) return;

  reportedMissingIds.add(columnId);
  DatadogService.captureErrorMessage(
    'Received line item column labels are missing a known canonical column id',
    { columnId }
  );
};

/** Test seam: clears the once-per-id log guard so each case starts from a known state. */
export const resetMissingColumnIdReports = (): void => {
  reportedMissingIds.clear();
};

interface CreateColumnLabelResolverParams {
  labels?: LineItemColumnLabels | null;
  t: TranslateLabel;
}

/**
 * Builds the resolver every paint point reads labels through.
 *
 * Unrecognised ids in `labels` are ignored rather than rejected, so the backend can add
 * columns without a lockstep client deploy. An absent map degrades to defaults silently —
 * that is an older backend, not a fault.
 *
 * Labels arrive already trimmed and validated, so the falsy check below only separates "an
 * override exists" from "no override"; it does not re-validate.
 *
 * Wrap this in `useColumnLabelResolver` inside components: the returned function's identity
 * must be stable across renders, or the grid recomputes its columns on every keystroke.
 */
export const createColumnLabelResolver = ({
  labels,
  t,
}: CreateColumnLabelResolverParams): ColumnLabelResolver => (columnId, fallbackLabel) => {
  if (labels && !(columnId in labels)) reportMissingColumnId(columnId);

  const override = labels?.[columnId];

  if (override) return override;

  return fallbackLabel ?? t(CLIENT_I18N_KEY[columnId]);
};
